import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Settings2, ChevronDown, ChevronUp, ChevronRight, X } from "lucide-react";
import { UserAPI } from "@shared/types";
import { isSapphicUser } from "@shared/quizAnswers";
import { useT } from "@/lib/i18n";

interface BookingDetailsSelectorProps {
  value: UserAPI.SpecialRequests;
  onChange: (details: UserAPI.SpecialRequests) => void;
  profile?: any;
  initiallyOpened: boolean;
}

type PreferenceOption<T extends string> = {
  value: T;
  label: string;
  subtitle?: string;
};

const budgetOptions: PreferenceOption<UserAPI.BudgetPreference>[] = [
  { value: "$", label: "$" },
  { value: "$$", label: "$$" },
  { value: "$$$", label: "$$$" },
];

function getAgeOptions(
  t: (key: string) => string
): PreferenceOption<UserAPI.AgePreference>[] {
  return [
    { value: "my_age", label: t('booking.preferences.options.age.myAge') },
    { value: "older", label: t('booking.preferences.options.age.older') },
    { value: "younger", label: t('booking.preferences.options.age.younger') },
  ];
}

function getGenderOptions(
  t: (key: string) => string,
  profile?: any
): PreferenceOption<UserAPI.GenderPreference>[] {
  const genderIdentity: string[] = profile?.genderIdentity || [];

  if (isSapphicUser(profile ?? {})) {
    return [
      { value: "sapphic", label: t('booking.preferences.options.gender.sapphic'), subtitle: t('booking.preferences.options.gender.sapphicSubtitle') },
      { value: "mixed", label: t('booking.preferences.options.gender.mixed'), subtitle: t('booking.preferences.options.gender.mixedSubtitle') },
    ];
  }
  const nonBinaryTable = genderIdentity.some((g: string) => 
    ['non_binary', 'genderqueer', 'other', 'prefer_not_say'].includes(g)
  );
  if (nonBinaryTable) {
    return [
      { value: "sapphic", label: t('booking.preferences.options.gender.sapphic'), subtitle: t('booking.preferences.options.gender.sapphicSubtitle') },
      { value: "male", label: t('booking.preferences.options.gender.male'), subtitle: t('booking.preferences.options.gender.maleSubtitle') },
      { value: "mixed", label: t('booking.preferences.options.gender.mixed'), subtitle: t('booking.preferences.options.gender.mixedSubtitle') },
    ];
  }
  // Default for men
  return [
    { value: "male", label: t('booking.preferences.options.gender.male'), subtitle: t('booking.preferences.options.gender.maleSubtitle') },
    { value: "mixed", label: t('booking.preferences.options.gender.mixed'), subtitle: t('booking.preferences.options.gender.mixedSubtitle') },
  ];
}

// Helper to get display text for selected values
function getDisplayText<T extends string>(
  t: (key: string) => string,
  value: T[] | undefined,
  options: PreferenceOption<T>[]
): string {
  if (!value || value.length === 0) return t('booking.preferences.any');
  const labels = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean);
  return labels.join(", ");
}

interface PreferenceModalProps<T extends string> {
  label: string;
  options: PreferenceOption<T>[];
  value: T[] | undefined;
  onChange: (value: T[] | undefined) => void;
  onClose: () => void;
  t: (key: string) => string;
}

function PreferenceModal<T extends string>({
  label,
  options,
  value,
  onChange,
  onClose,
  t,
}: PreferenceModalProps<T>) {
  const handleToggle = (optionValue: T) => {
    const currentValues = value ?? [];
    const isSelected = currentValues.includes(optionValue);

    if (isSelected) {
      const newValues = currentValues.filter((v) => v !== optionValue);
      onChange(newValues.length > 0 ? newValues : undefined);
    } else {
      onChange([...currentValues, optionValue]);
    }
  };

  const hasNoPreference = !value || value.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="card p-0 max-w-md overflow-hidden bg-white dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-divider dark:border-neutral-700">
          <h3 className="text-lg font-semibold dark:text-neutral-100">{label}</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
          </button>
        </div>

        {/* Options */}
        <div className="p-4 space-y-2">
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className={`w-full py-3 px-4 text-sm rounded-lg transition-all text-left ${
              hasNoPreference ? "select-option-selected" : "select-option"
            }`}
          >
            {t('booking.preferences.any')}
          </button>
          {options.map((option) => {
            const isSelected = value?.includes(option.value) ?? false;
            return (
              <button
                key={option.label}
                type="button"
                onClick={() => handleToggle(option.value)}
                className={`w-full py-3 px-4 text-sm rounded-lg transition-all text-left ${
                  isSelected ? "select-option-selected" : "select-option"
                }`}
              >
                <div>{option.label}</div>
                {option.subtitle && (
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    {option.subtitle}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Done button */}
        <div className="p-4 border-t border-divider dark:border-neutral-700">
          <button
            onClick={onClose}
            className="w-full btn btn-primary py-3"
          >
            {t('booking.preferences.done')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

interface PreferenceButtonProps<T extends string> {
  label: string;
  options: PreferenceOption<T>[];
  value: T[] | undefined;
  onChange: (value: T[] | undefined) => void;
  t: (key: string) => string;
}

function PreferenceButton<T extends string>({
  label,
  options,
  value,
  onChange,
  t,
}: PreferenceButtonProps<T>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const displayText = getDisplayText(t, value, options);
  const hasValue = value && value.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full flex items-center justify-between p-4 rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-divider dark:border-neutral-700 hover:border-primary-500/50 transition-all group"
      >
        <div className="text-left">
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-0.5">{label}</div>
          <div className={`text-sm font-medium ${hasValue ? "text-neutral-900 dark:text-neutral-100" : "text-neutral-500 dark:text-neutral-400"}`}>
            {displayText}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-neutral-500 dark:text-neutral-400 group-hover:text-primary-500 transition-colors" />
      </button>

      <AnimatePresence>
        {isModalOpen && (
          <PreferenceModal
            label={label}
            options={options}
            value={value}
            onChange={onChange}
            onClose={() => setIsModalOpen(false)}
            t={t}
          />
        )}
      </AnimatePresence>
    </>
  );
}

export function BookingDetailsSelector({
  value,
  onChange,
  profile,
  initiallyOpened,
}: BookingDetailsSelectorProps) {
  const { t } = useT();
  const [showSelector, setShowSelector] = useState<boolean | null>(
    initiallyOpened ?? null
  );

  const isOpened = showSelector ?? initiallyOpened;
  const ageOptions = getAgeOptions(t);
  const genderOptions = getGenderOptions(t, profile);

  // Generic handler for updating a preference field
  const handlePreferenceChange = <K extends keyof UserAPI.SpecialRequests>(
    key: K,
    newValue: UserAPI.SpecialRequests[K] | undefined
  ) => {
    const updated: UserAPI.SpecialRequests = { ...value };
    if (newValue === undefined) {
      delete updated[key];
    } else {
      updated[key] = newValue;
    }
    onChange(updated);
  };

  const handleTextChange = (text: string) => {
    handlePreferenceChange(
      "specialRequestsText",
      text.trim() ? text.slice(0, 160) : undefined
    );
  };

  const hasAnyValue =
    value?.specialRequestsText?.trim() ||
    value?.preferSapphicTable ||
    (value?.budget && value.budget.length > 0) ||
    (value?.age && value.age.length > 0) ||
    (value?.gender && value.gender.length > 0) ||
    (value?.language && value.language.length > 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <button
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between my-2 group"
      >
        <div className="flex items-center">
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-colors ${
              hasAnyValue ? "bg-primary-500/20" : "bg-dark-elevated"
            }`}
          >
            <Settings2
              className={`w-5 h-5 transition-colors ${
                hasAnyValue ? "text-primary-500" : "text-dark-muted"
              }`}
            />
          </div>
          <div className="text-left">
            <div className="text-xs text-dark-muted mb-0.5">{t('booking.preferences.title')}</div>
            <div className="text-sm font-semibold">
              {hasAnyValue ? t('booking.preferences.set') : t('booking.preferences.setYour')}
            </div>
          </div>
        </div>
        {isOpened ? (
          <ChevronUp className="w-5 h-5 text-dark-muted group-hover:text-primary-800 transition-colors" />
        ) : (
          <ChevronDown className="w-5 h-5 text-dark-muted group-hover:text-primary-800 transition-colors" />
        )}
      </button>

      {isOpened && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="space-y-3 pt-2 overflow-hidden"
        >
          {/* Preference Buttons */}
          <PreferenceButton
            label={t('booking.preferences.budget')}
            options={budgetOptions}
            value={value?.budget}
            onChange={(v) => handlePreferenceChange("budget", v)}
            t={t}
          />
          <PreferenceButton
            label={t('booking.preferences.age')}
            options={ageOptions}
            value={value?.age}
            onChange={(v) => handlePreferenceChange("age", v)}
            t={t}
          />
          <PreferenceButton
            label={t('booking.preferences.gender')}
            options={genderOptions}
            value={value?.gender}
            onChange={(v) => handlePreferenceChange("gender", v)}
            t={t}
          />
          {/* Special Requests Text Input */}
          <div className="space-y-2">
            <div className="text-xs text-dark-muted">{t('booking.preferences.additional')}</div>
            <textarea
              id="special-requests"
              value={value?.specialRequestsText ?? ""}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={t('booking.preferences.additionalPlaceholder')}
              rows={2}
              className="w-full px-4 py-3 rounded-lg bg-neutral-100 dark:bg-neutral-800 focus:bg-white dark:focus:bg-neutral-700 border border-divider dark:border-neutral-700 text-dark-text placeholder-dark-muted focus:outline-none focus:border-transparent resize-none"
            />
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
